// Importaciones de React y hooks necesarios
import React, { useState, useCallback } from 'react';

// Importaciones específicas de Lucide icons para mejor rendimiento
import { Car, X, Menu, Clock, Search, Navigation, Brain, Activity, Star, Square, Phone } from 'lucide-react';

// Tipos seguros para el estado
type MenuState = boolean;

// Componente principal de la aplicación
function App(): JSX.Element {
  // Estados para controlar la interfaz con tipos seguros
  const [menuMobileVisible, setMenuMobileVisible] = useState<MenuState>(false);

  // Manejadores de eventos seguros usando useCallback para prevenir recreaciones innecesarias
  const manejarInicioSesion = useCallback((): void => {
    // TODO: Implementar integración con sistema de autenticación
    if (process.env.NODE_ENV === 'development') {
      console.log('Usuario intentó iniciar sesión');
    }
  }, []);

  const manejarDescargaApp = useCallback((): void => {
    // TODO: Implementar redirección a tienda de aplicaciones según plataforma
    if (process.env.NODE_ENV === 'development') {
      console.log('Usuario intentó descargar la app');
    }
  }, []);

  const manejarReserva = useCallback((): void => {
    // TODO: Implementar flujo de reserva de estacionamiento
    if (process.env.NODE_ENV === 'development') {
      console.log('Usuario intentó realizar una reserva');
    }
  }, []);

  // Manejador seguro para el toggle del menú
  const toggleMenu = useCallback((): void => {
    setMenuMobileVisible(prev => !prev);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section con atributos de seguridad */}
      <div className="relative h-screen">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1506521781263-d8422e82f27a?auto=format&fit=crop&q=80&w=1920&h=1080"
            alt="Estacionamiento moderno con sistema de gestión inteligente"
            className="w-full h-full object-cover opacity-50"
            loading="eager"
            width={1920}
            height={1080}
            referrerPolicy="no-referrer"
            crossOrigin="anonymous"
          />
        </div>

        <div className="relative z-10 container mx-auto px-4 h-full pt-24">
          <nav
            className="fixed top-0 left-0 right-0 w-full py-6 flex justify-between items-center bg-black/80 backdrop-blur-sm z-50 px-4"
            role="navigation"
            aria-label="Navegación principal"
          >
            <div className="container mx-auto flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <Car className="w-8 h-8" aria-hidden="true" />
                <span className="text-2xl font-bold">EstacionAI</span>
              </div>

              <button
                className="md:hidden p-2"
                onClick={toggleMenu}
                aria-expanded={menuMobileVisible}
                aria-label={menuMobileVisible ? 'Cerrar menú' : 'Abrir menú'}
                type="button"
              >
                {menuMobileVisible ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
              </button>

              <div
                className={`${menuMobileVisible ? 'block' : 'hidden'} md:flex space-y-4 md:space-y-0 md:space-x-8 absolute md:relative top-20 md:top-0 left-0 md:left-auto w-full md:w-auto bg-black/80 md:bg-transparent p-4 md:p-0`}
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="menu-button"
              >
                <a href="#features" className="hover:text-blue-400 block" role="menuitem">Características</a>
                <a href="#how-it-works" className="hover:text-blue-400 block" role="menuitem">Cómo Funciona</a>
                <a href="#benefits" className="hover:text-blue-400 block" role="menuitem">Beneficios</a>
                <a href="#contact" className="hover:text-blue-400 block" role="menuitem">Contacto</a>
              </div>
            </div>

            {/* Menú móvil con manejo seguro de eventos  <button 
              className="bg-blue-600 px-6 py-2 rounded-full hover:bg-blue-700 transition"
              onClick={manejarInicioSesion}
              aria-label="Iniciar sesión en la aplicación"
              type="button"
            >
              Iniciar Sesión
            </button>*/}
          </nav>

          <div className="flex flex-col justify-center h-[calc(100vh-100px)]">
            <h1 className="text-4xl md:text-7xl font-bold mb-6">
              Encuentra y agenda tu lugar perfecto para estacionar en segundos
            </h1>
            <p className="text-lg md:text-2xl mb-8 max-w-2xl">
              Localiza estacionamientos con disponibilidad en tiempo real y reserva tu espacio desde tu teléfono.
            </p>
            <div className="flex flex-col md:flex-row gap-4">
              <a
                href="https://chat.estacionai.cl/"
                className="bg-blue-600 px-8 py-4 rounded-full text-lg hover:bg-blue-700 transition text-center"
                aria-label="Ir al chat de EstacionAI"
                rel="noopener noreferrer"
              >
                Prueba nuestro Chatbot
              </a>
              <button
                className="border border-white px-8 py-4 rounded-full text-lg hover:bg-white hover:text-black transition"
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                aria-label="Ver características principales"
                type="button"
              >
                Más Información
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Características Principales Section */}
      <div id="features" className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16">Características Principales</h2>
          <div className="grid md:grid-cols-3 gap-12">
            <div className="bg-gray-800 p-8 rounded-xl">
              <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-8 h-8" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Disponibilidad en Tiempo Real</h3>
              <p className="text-gray-400 text-center">
                Consulta los cupos disponibles antes de llegar, evitando pérdidas de tiempo en la búsqueda de estacionamiento.
              </p>
            </div>
            <div className="bg-gray-800 p-8 rounded-xl">
              <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-8 h-8" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Reserva con Un Clic</h3>
              <p className="text-gray-400 text-center">
                Asegura tu lugar con solo un clic, garantizando tu espacio cuando lo necesites sin complicaciones.
              </p>
            </div>
            <div className="bg-gray-800 p-8 rounded-xl">
              <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Navigation className="w-8 h-8" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-center">Navegación GPS Integrada</h3>
              <p className="text-gray-400 text-center">
                Obtén indicaciones precisas hasta tu espacio reservado con nuestra navegación GPS integrada.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Gestión Inteligente con IA Section */}
      <div className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16">Gestión Inteligente con IA</h2>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="grid grid-cols-2 gap-8">
              <div className="space-y-4">
                <Brain className="w-12 h-12 text-blue-400" aria-hidden="true" />
                <h3 className="text-xl font-bold">Predicción de Demanda</h3>
                <p className="text-gray-400">Nuestra IA analiza patrones para optimizar la disponibilidad de espacios.</p>
              </div>
              <div className="space-y-4">
                <Activity className="w-12 h-12 text-blue-400" aria-hidden="true" />
                <h3 className="text-xl font-bold">Control de Tráfico</h3>
                <p className="text-gray-400">Gestión inteligente del flujo de vehículos para reducir congestiones.</p>
              </div>
              <div className="space-y-4">
                <Star className="w-12 h-12 text-blue-400" aria-hidden="true" />
                <h3 className="text-xl font-bold">Asignación Dinámica</h3>
                <p className="text-gray-400">Distribución eficiente de espacios según la demanda en tiempo real.</p>
              </div>
              <div className="space-y-4">
                <Square className="w-12 h-12 text-blue-400" aria-hidden="true" />
                <h3 className="text-xl font-bold">Detección de Espacios</h3>
                <p className="text-gray-400">Sensores inteligentes monitorizan en tiempo real la ocupación de cada plaza.</p>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?auto=format&fit=crop&q=80&w=800&h=600"
                alt="Sistema de gestión inteligente con pantallas de monitoreo"
                className="rounded-lg shadow-2xl"
                loading="eager"
                width={800}
                height={600}
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Beneficios para Conductores Section */}
      <div id="benefits" className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16">Beneficios para Conductores</h2>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <img
              src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&q=80&w=800&h=600"
              alt="Conductor feliz"
              className="rounded-lg shadow-2xl"
              loading="eager"
              width={800}
              height={600}
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
            />
            <div className="space-y-8">
              <div className="bg-gray-800 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-2">Ahorro de Tiempo</h3>
                <p className="text-gray-400">Olvídate de dar vueltas buscando estacionamiento. Con EstacionAI, tu espacio te espera.</p>
              </div>
              <div className="bg-gray-800 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-2">Reducción de Estrés</h3>
                <p className="text-gray-400">Elimina la incertidumbre y la ansiedad asociada con encontrar estacionamiento en zonas concurridas.</p>
              </div>
              <div className="bg-gray-800 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-2">Planificación Eficiente</h3>
                <p className="text-gray-400">Organiza mejor tu día sabiendo exactamente dónde y cuándo podrás estacionar.</p>
              </div>
              <div className="bg-gray-800 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-2">Experiencia Personalizada</h3>
                <p className="text-gray-400">Recibe recomendaciones basadas en tus preferencias y patrones de estacionamiento.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cómo Funciona Section */}
      <div id="how-it-works" className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16">Cómo Funciona EstacionAI</h2>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-12">
              <div className="flex items-start space-x-4">
                <div className="bg-blue-600 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Sensores Inteligentes</h3>
                  <p className="text-gray-400">Detectan la ocupación de cada espacio en tiempo real.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-blue-600 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Procesamiento de Datos</h3>
                  <p className="text-gray-400">La IA analiza la información para predecir patrones y optimizar el uso.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-blue-600 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Actualización en Tiempo Real</h3>
                  <p className="text-gray-400">Los usuarios reciben información actualizada constantemente.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-blue-600 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Reserva y Guía</h3>
                  <p className="text-gray-400">El sistema asigna espacios y guía a los conductores hasta su lugar.</p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1573600073955-f15b3b6caab7?auto=format&fit=crop&q=80&w=800&h=600"
                alt="Sistema de estacionamiento inteligente"
                className="rounded-lg shadow-2xl"
                loading="eager"
                width={800}
                height={600}
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div id="contact" className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-8">Próximamente</h2>
          <p className="text-xl mb-12 text-gray-400">Estará disponible en Android e iOS</p>
          <div className="flex flex-row justify-center gap-6">
            <div className="flex items-center space-x-2 text-blue-600">
              <Phone className="w-6 h-6" aria-hidden="true" />
              <span className="text-lg">Android</span>
            </div>
            <div className="flex items-center space-x-2 text-blue-600">
              <Phone className="w-6 h-6" aria-hidden="true" />
              <span className="text-lg">iOS</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-8 md:mb-0">
              <Car className="w-8 h-8" aria-hidden="true" />
              <span className="text-2xl font-bold">EstacionAI</span>
            </div>
            <div className="flex space-x-8">
              <a href="#features" className="hover:text-blue-400">Características</a>
              <a href="#how-it-works" className="hover:text-blue-400">Cómo Funciona</a>
              <a href="#benefits" className="hover:text-blue-400">Beneficios</a>
              <a href="#contact" className="hover:text-blue-400">Contacto</a>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 EstacionAI. Todos los derechos reservados.</p>
            <p>Iwie Space</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;